<template>
  <v-dialog :value="dialog" persistent max-width="700px">
    <v-card v-if="dialog">
      <v-card-title>
        <span class="headline">{{ $t("itinerary.exporter.title") }}</span>
      </v-card-title>
      <v-card-subtitle class="mx-2 mt-1">{{ $t("itinerary.exporter.subtitle") }} </v-card-subtitle>

      <div v-if="!activity">
        <v-card-text>
          <p>{{ $t("itinerary.exporter.noActivities") }}</p>
        </v-card-text>
        <v-card-actions class="inidgo">
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="close">{{ $t("common.close") }}</v-btn>
        </v-card-actions>
      </div>
      <div v-else>
        <v-card-text>
          <v-row dense>
            <v-col cols="12">
              <v-text-field
                :label="$t('common.name')"
                disabled
                :value="currentTrip.name"
              ></v-text-field>
            </v-col>
            <v-col cols="6" md="4">
              <v-text-field
                class="my-0"
                :label="$t('common.firstActivity')"
                disabled
                :value="activity.name"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                class="my-0"
                :label="$t('common.date')"
                disabled
                :value="activity.sortingDate | zonedDate('P', activity.display_timezone)"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                class="my-0"
                :label="$t('common.time.time')"
                disabled
                :value="activity.sortingDate | zonedDate('p', activity.display_timezone)"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-title class="mt-n4">
          <span class="headline">{{ $t("itinerary.exporter.export") }}</span>
        </v-card-title>
        <v-card-text>
          <v-select
            class="my-0"
            :items="trips"
            v-model="cloneTrip"
            return-object
            item-text="name"
            item-value="id"
            :label="this.$t('itinerary.exporter.selectLabel')"
          ></v-select>

          <v-row dense>
            <v-col cols="4">
              <v-text-field
                class="my-0"
                :label="$t('common.exportActivity')"
                disabled
                :value="activity.name"
              ></v-text-field>
            </v-col>
            <v-col cols="4">
              <DatePickerField
                v-model="startDate.date"
                persistentHint
                :hint="this.$t('itinerary.exporter.datePickHint') + ' ' + this.activity.name"
                :label="$t('common.date')"
              />
            </v-col>
            <v-col cols="4">
              <TimePickerField
                v-model="startDate.time"
                persistentHint
                :hint="this.$t('itinerary.exporter.datePickHint') + ' ' + this.activity.name"
                :label="$t('common.time.time')"
              />
            </v-col>
            <v-col cols="12">
              <h3>{{ $t("itinerary.exporter.alsoInclude") }}</h3>
            </v-col>
            <v-col cols="4">
              <v-checkbox
                color="primary"
                :hint="this.$t('itinerary.exporter.citiesVisitingHint')"
                persistent-hint
                v-model="citiesCheckbox"
                :label="this.$t('itinerary.exporter.citiesVisitingLabel')"
              ></v-checkbox>
            </v-col>

            <v-col cols="4">
              <v-checkbox
                color="primary"
                :hint="this.$t('itinerary.exporter.mapSavedHint')"
                persistent-hint
                v-model="mapsCheckbox"
                :label="this.$t('itinerary.exporter.mapSavedLabel')"
              ></v-checkbox>
            </v-col>
            <v-col cols="4">
              <v-checkbox
                color="primary"
                :hint="this.$t('itinerary.exporter.contactsSavedHint')"
                persistent-hint
                v-model="contactsCheckbox"
                :label="this.$t('itinerary.exporter.contactsSavedLabel')"
              ></v-checkbox>
            </v-col>
            <v-col cols="6">
              <v-checkbox
                color="primary"
                :hint="this.$t('itinerary.exporter.companyAdminsHint')"
                persistent-hint
                v-model="companyAdminsCheckbox"
                :label="this.$t('itinerary.exporter.companyAdminsLabel')"
              ></v-checkbox>
            </v-col>
            <v-col cols="4">
              <v-checkbox
                color="primary"
                :hint="this.$t('itinerary.exporter.adminsHint', { n: consumingLicences })"
                persistent-hint
                v-model="adminsCheckbox"
                :label="this.$t('itinerary.exporter.adminsLabel')"
              ></v-checkbox>
            </v-col>
          </v-row>
          <p class="mt-2" v-if="cloneTrip && startDate.date && startDate.time">
            <span class="font-weight-bold">{{ activity.name }}</span>
            {{ $t("itinerary.exporter.willNotStart") }}
            <span class="font-weight-bold">{{ cloneTrip.name }}</span>
            {{ $t("itinerary.exporter.at") }}
            <span class="primary--text"
              >{{ startDate.date | strToDate("P") }}, {{ startDate.time }}</span
            >
          </p>
          <p class="red--text text-right" v-show="feedback">{{ feedback }}</p>
        </v-card-text>
        <v-card-actions class="inidgo">
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="close">{{ $t("common.cancel") }}</v-btn>
          <v-btn color="primary" @click="save">{{ $t("common.save") }}</v-btn>
        </v-card-actions>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { format, utcToZonedTime } from "date-fns-tz";
import { parseISO } from "date-fns";
import DatePickerField from "@/components/Base/DatePickerField";
import TimePickerField from "@/components/Base/TimePickerField";
import moment from "moment-timezone";

import groupCopier from "@/APIS/group-copier";

export default {
  name: "ItineraryExporter",
  props: ["dialog"],
  components: { DatePickerField, TimePickerField },
  data() {
    return {
      startDate: {},
      cloneTrip: null,
      feedback: null,
      companyAdminsCheckbox: false,
      adminsCheckbox: false,
      menuStartDate: false,
      menuStartTime: false,
      citiesCheckbox: true,
      mapsCheckbox: true,
      contactsCheckbox: true,
    };
  },
  methods: {
    save() {
      if (!this.cloneTrip) {
        this.feedback = this.$t("itinerary.exporter.feedback1");
        return;
      }
      if (!this.startDate.date) {
        this.feedback = this.$t("itinerary.exporter.feedback2");
        return;
      }
      if (!this.startDate.time) {
        this.feedback = this.$t("itinerary.exporter.feedback3");
        return;
      }
      this.feedback = null;

      const newDate = parseISO(`${this.startDate.date} ${this.startDate.time}`);

      const tzDifference =
        moment.tz.zone(this.activity.display_timezone).utcOffset(newDate.getTime()) -
        newDate.getTimezoneOffset();

      const offset =
        newDate.getTime() - this.activity.sortingDate.getTime() + tzDifference * 60 * 1000;
      groupCopier(
        this.currentTrip.id,
        this.cloneTrip.id,
        offset,
        this.mapsCheckbox,
        this.citiesCheckbox,
        this.contactsCheckbox,
        this.adminsCheckbox,
      );

      this.close();
    },
    close() {
      this.$emit("closed");
    },
  },
  watch: {
    cloneTrip(trip) {
      if (this.startDate.date) return;
      this.startDate = {
        date: format(utcToZonedTime(trip.start_date.toDate(), "utc"), "yyyy-MM-dd"),
        time: format(
          utcToZonedTime(this.activity.sortingDate, this.activity.display_timezone),
          "HH:mm",
        ),
      };
    },
  },
  computed: {
    trips() {
      return this.$store.getters.activeTrips
        .filter((trip) => trip.id !== this.currentTrip.id)
        .sort((a, b) => (a.start_date.seconds > b.start_date.seconds ? -1 : 1));
    },
    consumingLicences() {
      const trip = this.currentTrip;
      const company = this.$store.state.companies[trip.company];
      const companyAdmins = Object.keys(company.admins);
      const tripAdmins = [];
      Object.entries(trip.user_ids).forEach(([key, isAdmin]) => {
        if (isAdmin) {
          tripAdmins.push(key);
        }
      });

      return tripAdmins.filter((admin) => !companyAdmins.includes(admin)).length;
    },
    activity() {
      return this.$store.getters.sortedItinerary[0];
    },
    currentTrip() {
      return this.$store.state.trip.trip;
    },
  },
};
</script>
